import Drift from 'drift-zoom';
import EventHandler from '@pixelunion/events';
import rimg from 'rimg-shopify';

import layout from '../Layout';
import Video from './Video';
import ProductClicktoZoom from './ProductClickToZoom';

export default class ProductGallery {
  constructor({ el, settings, isQuickshop }) {
    this.el = el;
    this.settings = settings;
    this.isQuickshop = isQuickshop || false;

    this.slider = this.el.querySelector('[data-product-slider]');
    this.slides = this.slider.querySelectorAll('[data-product-image]');
    this.videoSlides = this.slider.querySelectorAll('[data-product-image-video]');
    this.zoomButton = this.slider.querySelector('[data-gallery-expand]');
    this.thumbnailsWrapper = this.el.querySelector('[data-product-slider-pagination]');
    this.thumbnails = this.el.querySelectorAll('[data-product-thumbnail]');

    this.selectedSlide = this.slider.querySelector('.image--selected');
    this.selectedThumbnail = this.thumbnailsWrapper.querySelector('.thumbnail--selected');
    this.selectedVideo = null;
    this.selectedIndex = Array.prototype.indexOf.call(this.slides, this.selectedSlide);

    this.variantChange = this._onVariantChange.bind(this);

    this.events = new EventHandler();

    this.events.register(this.thumbnailsWrapper, 'click', e => this._thumbnailClicked(e));
    this.events.register(window, 'resize', () => this._adjustGalleryPositioning());

    if (this.settings.click_to_zoom !== 'disabled') {
      this.productZoom = null;

      const zoomClicked = e => {
        e.stopPropagation();
        this._openProductZoom();
      };

      this.events.register(this.zoomButton, 'click', e => zoomClicked(e));
      this.events.register(this.slider, 'click', e => zoomClicked(e));
    }

    if (this.selectedSlide.hasAttribute('data-product-image-video')) {
      const videoEl = this.selectedSlide.querySelector('[data-video]');
      this.selectedVideo = new Video(videoEl);
    } else if (this.settings.hover_zoom !== 'disabled') {
      if (layout.isGreaterThanBreakpoint('L', true)) {
        this._setupHoverZoom();
      }
    }

    if (this.isQuickshop) {
      rimg.watch(this.el);
    }

    this._adjustGalleryPositioning();
  }

  _selectSlide(slide) {
    this.selectedSlide.classList.remove('image--selected');
    this.selectedSlide = slide;
    this.selectedSlide.classList.add('image--selected');

    const imageIndex = this.selectedSlide.dataset.productImageIndex;
    this.selectedIndex = parseInt(imageIndex, 10);

    this.selectedThumbnail.classList.remove('thumbnail--selected');
    this.selectedThumbnail = this.thumbnails[imageIndex];
    this.selectedThumbnail.classList.add('thumbnail--selected');

    if (this.selectedVideo) {
      this.selectedVideo.unload();
    }
    this.selectedVideo = null;

    this._disableHoverZoom();

    if (this.selectedSlide.hasAttribute('data-product-image-video')) {
      const videoEl = this.selectedSlide.querySelector('[data-video]');
      this.selectedVideo = new Video(videoEl);
      this.selectedVideo.onPauseClick();
      if (this.settings.enable_video_autoplay) {
        this.selectedVideo.autoplay();
      }
    } else if (this.settings.hover_zoom !== 'disabled') {
      if (layout.isGreaterThanBreakpoint('L', true)) {
        this._setupHoverZoom();
      }
    }

    this._adjustGalleryPositioning();
  }

  _onVariantChange(variant) {
    if (!variant.featured_image) {
      return;
    }

    const slide = this.slider.querySelector(`[data-product-image="${variant.featured_image.id}"]`);
    this._selectSlide(slide);
  }

  _setupHoverZoom() {
    if (document.documentElement.classList.contains('has-touch')) return;

    const selectedImage = this.selectedSlide.querySelector('[data-rimg]');
    selectedImage.setAttribute('data-zoom', this.selectedSlide.getAttribute('data-zoom'));

    this.drift = new Drift(selectedImage, {
      paneContainer: this.settings.hover_zoom === 'separate'
        ? this.el.querySelector('[data-zoomed-image]')
        : this.selectedSlide,
      inlinePane: false,
      hoverBoundingBox: this.settings.hover_zoom === 'separate',
      handleTouch: false,
      onShow: () => {
        if (this.settings.hover_zoom === 'separate') {
          const productMain = document.querySelector('.product-main');
          const productAlt = document.querySelector('.product-form--alt');
          productMain.classList.add('product-gallery--fade');
          if (productAlt) productAlt.classList.add('product-gallery--fade');
        }
      },
      onHide: () => {
        if (this.settings.hover_zoom === 'separate') {
          const productMain = document.querySelector('.product-main');
          const productAlt = document.querySelector('.product-form--alt');
          productMain.classList.remove('product-gallery--fade');
          if (productAlt) productAlt.classList.remove('product-gallery--fade');
        }
      },
    });
  }

  _disableHoverZoom() {
    if (this.drift) {
      this.drift.disable();
      this.drift = null;
    }
  }

  _thumbnailClicked(e) {
    let button;

    // The target will be the button itself if the user is using a keyboard to navigate, but
    // otherwise it will be the image that is clicked on.
    if (e.target.classList.contains('product-gallery--thumbnail-trigger')) {
      button = e.target;
    } else if (e.target.parentNode.parentNode.classList
      .contains('product-gallery--thumbnail-trigger')) {
      button = e.target.parentNode.parentNode;
    } else {
      return;
    }

    const slide = this.slides[button.getAttribute('data-product-thumbnail')];
    this._selectSlide(slide);
  }

  _setSlideHeight() {
    this.thumbnailsWrapper.classList.remove('loading');

    const containerHeight = this.selectedSlide.getBoundingClientRect().height;
    this.slider.style.height = `${containerHeight}px`;
  }

  _adjustGalleryPositioning() {
    // We need to adjust the width of the thumbnails if they are positioned left
    // and they wrap into 2 columns. CSS flexbox does not cause the width of the
    // element to stretch. Looks like it's a problem with flexbox when wrapping columns:
    // https://stackoverflow.com/questions/33891709/when-flexbox-items-wrap-in-column-mode-container-does-not-grow-its-width
    if (this.settings.thumbnail_position === 'left') {
      if (this.isQuickshop) {
        if (layout.isGreaterThanBreakpoint('L', true)) {
          this.el.classList.add('product-gallery--thumbs-left');
        } else {
          this.el.classList.remove('product-gallery--thumbs-left');
        }
      }

      if (layout.isGreaterThanBreakpoint('L', true)
        || (layout.isGreaterThanBreakpoint('S', true) && !this.isQuickshop)) {
        if (this.el.firstChild !== this.thumbnailsWrapper) {
          const activeButton = document.activeElement;
          this.el.insertBefore(this.thumbnailsWrapper, this.slider);
          activeButton.focus(); // This prevents loss of focus if one of the thumbnails are selected
        }

        // When switching layouts in the editor sometimes we need to wait for a few milliseconds
        // before positioning the thumbs.
        setTimeout(() => {
          const leftmostBounds = this.thumbnails[0].getBoundingClientRect();
          const thumbStyle = this.thumbnails[0].currentStyle
            || window.getComputedStyle(this.thumbnails[0]);
          const thumbMargin = (parseInt(thumbStyle.marginLeft, 10)) * 2;
          const wrapped = Array.prototype.some.call(this.thumbnails,
            thumb => thumb.getBoundingClientRect().left > leftmostBounds.left);
          if (wrapped) {
            this.thumbnailsWrapper.style.width = `${(leftmostBounds.width + thumbMargin) * 2}px`;
            this.thumbnailsWrapper.style.maxWidth = `${(leftmostBounds.width + thumbMargin) * 2}px`;
          }
          this._setSlideHeight();
        }, 100);
      } else if (this.slider.nextSibling !== this.thumbnailsWrapper) {
        this.el.appendChild(this.thumbnailsWrapper);
        this.thumbnailsWrapper.style.width = '';
        this.thumbnailsWrapper.style.maxWidth = '';
        this._setSlideHeight();
      }
    } else {
      this._setSlideHeight();
    }
  }

  _openProductZoom() {
    if (this.selectedSlide.hasAttribute('data-product-image-video')
      || (layout.isGreaterThanBreakpoint('S', true) && this.settings.click_to_zoom === 'mobile')
      || (layout.isLessThanBreakpoint('XS', true) && this.settings.click_to_zoom === 'desktop')) {
      return;
    }

    this.productZoom = new ProductClicktoZoom({
      settings: this.settings,
      slides: this.slides,
      originalThumbnails: this.thumbnails,
      startIndex: this.selectedIndex,
      selectGallerySlide: slide => this._selectSlide(slide),
    });
  }

  unload() {
    this.events.unregisterAll();

    if (this.isQuickshop) {
      rimg.unwatch(this.el);
    }

    if (this.selectedVideo) {
      this.selectedVideo.unload();
    }

    this._disableHoverZoom();

    if (this.productZoom) {
      this.productZoom.unload();
    }
  }
}
